import React, { useState } from "react";
import { Loader2, Lock, Truck, Store, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { SHIPPING_FEE_PIECES, SHIPPING_FREE_THRESHOLD_PIECES } from "@/lib/pieces";

const eur = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const input = "w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-[#2877C2]";
const DEVIS_SENT_KEY = "lmb_devis_sent";

function Choice({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex-1 text-left p-3 border-2 rounded-sm transition-colors ${active ? "border-[#D8B86A] bg-[#FFFBF0]" : "border-gray-200 hover:border-gray-300"}`}>
      <div className="flex items-center gap-2 font-bold text-sm text-[#072135]"><Icon size={15} className="text-[#D8B86A]" /> {title}</div>
      <div className="text-[11px] text-[#4B5563] mt-0.5">{sub}</div>
    </button>
  );
}

export default function CheckoutForm({ pieces, others, onBack, onDone }) {
  const [client, setClient] = useState({ nom: "", email: "", telephone: "", adresse: "", code_postal: "", ville: "", message: "" });
  const [livraison, setLivraison] = useState("livraison");
  const [paiement, setPaiement] = useState("carte");
  const [loading, setLoading] = useState(false);

  const sousTotal = pieces.reduce((s, i) => s + (i.prix_ttc || 0) * i.qty, 0);
  const port = livraison === "livraison" && sousTotal < SHIPPING_FREE_THRESHOLD_PIECES ? SHIPPING_FEE_PIECES : 0;
  const set = (k) => (e) => setClient({ ...client, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: pieces.map((i) => ({ slug: i.slug, qty: i.qty })),
        client, mode_livraison: livraison, paiement, origin_url: window.location.origin,
      });
      const othersKey = JSON.stringify(others.map((i) => [i.type, i.slug, i.qty]));
      if (others.length && sessionStorage.getItem(DEVIS_SENT_KEY) !== othersKey) {
        await api.post("/devis-panier", {
          ...client, horaire_contact: "indifferent",
          message: `${client.message ? client.message + "\n" : ""}(Envoyé avec la commande de pièces ${data.numero || ""})`,
          items: others.map((i) => ({ slug: i.slug, type: i.type, nom: i.nom, prix: i.prix_ttc || i.prix_ht || 0, prix_unite: i.prix_ttc ? "TTC" : "HT", qty: i.qty })),
        }).then(() => sessionStorage.setItem(DEVIS_SENT_KEY, othersKey)).catch(() => {});
      }
      if (data.mode === "carte") {
        onDone({ redirecting: true });
        window.location.href = data.checkout_url;
      } else {
        onDone({ virement: data, devisSent: others.length > 0 });
      }
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Erreur, veuillez réessayer");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="checkout-form">
      <div className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#364757]">Réception</div>
        <div className="flex gap-2">
          <Choice testid="checkout-livraison" active={livraison === "livraison"} onClick={() => setLivraison("livraison")} icon={Truck} title="Livraison"
            sub={sousTotal >= SHIPPING_FREE_THRESHOLD_PIECES ? "Offerte" : `${eur.format(SHIPPING_FEE_PIECES)} · offerte dès ${SHIPPING_FREE_THRESHOLD_PIECES} €`} />
          <Choice testid="checkout-retrait" active={livraison === "retrait"} onClick={() => setLivraison("retrait")} icon={Store} title="Retrait atelier" sub="Bourgoin-Jallieu · gratuit" />
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#364757]">Vos coordonnées</div>
        <input data-testid="checkout-nom" required placeholder="Nom complet *" value={client.nom} onChange={set("nom")} className={input} />
        <input data-testid="checkout-email" type="email" required placeholder="Email *" value={client.email} onChange={set("email")} className={input} />
        <input data-testid="checkout-tel" required placeholder="Téléphone *" value={client.telephone} onChange={set("telephone")} className={input} />
        {livraison === "livraison" && (
          <>
            <input data-testid="checkout-adresse" required placeholder="Adresse de livraison *" value={client.adresse} onChange={set("adresse")} className={input} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="checkout-cp" required placeholder="Code postal *" value={client.code_postal} onChange={set("code_postal")} className={input} />
              <input data-testid="checkout-ville" required placeholder="Ville *" value={client.ville} onChange={set("ville")} className={input} />
            </div>
          </>
        )}
        <textarea data-testid="checkout-message" rows={2} placeholder="Message (optionnel)" value={client.message} onChange={set("message")} className={`${input} resize-none`} />
      </div>

      <div className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#364757]">Paiement</div>
        <div className="flex gap-2">
          <Choice testid="checkout-pay-carte" active={paiement === "carte"} onClick={() => setPaiement("carte")} icon={CreditCard} title="Carte / PayPal" sub="CB, Visa, Mastercard, PayPal, Apple Pay" />
          <Choice testid="checkout-pay-virement" active={paiement === "virement"} onClick={() => setPaiement("virement")} icon={Landmark} title="Virement" sub="Expédié à réception" />
        </div>
      </div>

      <div className="bg-[#F9F9F9] border border-gray-200 p-3 rounded-sm text-xs space-y-1" data-testid="checkout-summary">
        {pieces.map((i) => (
          <div key={i.slug} className="flex justify-between gap-2"><span>{i.qty}× {i.nom}</span><span className="font-mono-tech">{eur.format((i.prix_ttc || 0) * i.qty)}</span></div>
        ))}
        <div className="flex justify-between pt-1 border-t border-gray-300"><span>{livraison === "retrait" ? "Retrait atelier" : "Frais de port"}</span>
          <span className="font-mono-tech font-bold" data-testid="checkout-port">{port ? eur.format(port) : "Offert"}</span></div>
        <div className="flex justify-between items-baseline"><span className="font-bold text-sm">Total à payer</span><span className="lmb-price text-xl" data-testid="checkout-total">{eur.format(sousTotal + port)}</span></div>
        {others.length > 0 && (
          <p className="text-[11px] text-[#B45309] pt-1" data-testid="checkout-others-note">
            + {others.length} autre(s) article(s) (accessoires / mini-pelles) envoyé(s) en demande de devis, sans paiement.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="px-4 py-2.5 border border-gray-300 rounded-sm text-sm">Retour</button>
        <button data-testid="checkout-submit" type="submit" disabled={loading} className="lmb-btn-primary flex-1">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
          {paiement === "carte" ? `Payer ${eur.format(sousTotal + port)}` : "Valider la commande"}
        </button>
      </div>
      <p className="text-[10px] text-center text-[#4B5563]">Paiement sécurisé par Stripe. Vos données bancaires ne transitent jamais par notre site.</p>
    </form>
  );
}
